import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  TextField,
  Button,
  Container,
  Paper
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useConfirmation } from '../../../contexts/ConfirmationContext';
import { categoryApi } from '../../../services/mle-api';
import { getAppTheme } from '../../../services/authContext';

// --- CATEGORY MANAGEMENT PAGE COMPONENT ---
const CategoryManagement = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCategoryName, setNewCategoryName] = useState('');

    const { showSnackbar } = useSnackbar();
    const showConfirmation = useConfirmation();
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            const response = await categoryApi.list();
            setCategories(response.data.results || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            showSnackbar('Failed to fetch categories.', { severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) {
            showSnackbar('Category name cannot be empty.', { severity: 'warning' });
            return;
        }
        try {
            const slug = newCategoryName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
            await categoryApi.create({ name: newCategoryName.trim(), slug: slug });
            showSnackbar('Category added successfully', { severity: 'success' });
            setNewCategoryName('');
            fetchCategories(); // Refresh the list
        } catch (error) {
            showSnackbar(error.response?.data?.name?.[0] || 'Failed to add category', { severity: 'error' });
        }
    };

    const handleDeleteCategory = async (category) => {
        const confirmed = await showConfirmation({
            title: 'Delete Category',
            message: `Are you sure you want to delete "${category.name}"? Posts in this category will be moved to 'Uncategorized'.`,
            confirmText: 'Delete',
            confirmButtonColor: 'error',
        });
        if (confirmed) {
            try {
                await categoryApi.delete(category.id);
                showSnackbar('Category deleted successfully', { severity: 'success' });
                fetchCategories(); // Refresh the list
            } catch (error) {
                showSnackbar('Failed to delete category', { severity: 'error' });
            }
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" component="h1" fontWeight="bold">
                        Category Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Add or remove blog post categories.
                    </Typography>
                </Box>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Existing Categories</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, minHeight: '60px', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {loading ? (
                             <Typography variant="body2" color="text.secondary">Loading categories...</Typography>
                        ) : categories.length > 0 ? (
                            categories.map(cat => (
                                <Chip key={cat.id} label={cat.name} onDelete={() => handleDeleteCategory(cat)} />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">No categories found.</Typography>
                        )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>Add New Category</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            label="New Category Name"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                            fullWidth
                        />
                        <Button variant="contained" onClick={handleAddCategory}>Add</Button>
                    </Box>
                </Paper>
            </Container>
        </ThemeProvider>
    );
};

export default CategoryManagement;
